import React, { useMemo } from 'react'
import styled from 'styled-components'
import { HolviAuthorizeButton } from '@containers/HolviAuthorizeButton/HolviAuthorizeButton.tsx'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FeatureWrapper, useBusinessContext } from '@context'
import { deleteHolviIntegration, fetchHolviAccounts } from '@query/holvi.ts'
import { AnimatedContentLoader } from '@components'
import { HolviAccountMapper } from '@pages/SettingsPage/sections/HolviIntegrationSettings/HolviAccountMapper.tsx'
import { Trans, useTranslation } from 'react-i18next'
import toaster from 'react-hot-toast'
import { FaTrash } from 'react-icons/fa'
import { HolviPromoCard } from '@root/containers/HolviPromoCard'
import { BusinessForms } from '@root/query'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { getSettingsPageUrl } from '@constants'
import { getAbsoluteUrl } from '@utils/getAbsoluteUrl'

export const HolviIntegrationSettings: React.FC = () => {
  const { t } = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()

  const { data, isLoading: isAccountsLoading } = useQuery({
    queryKey: [slug, 'holvi', 'accounts'],
    queryFn: () => fetchHolviAccounts({ slug }),
    ...{
      enabled: business?.holvi_bookkeeping_api_enabled
    }
  })
  const accounts = data?.results

  const deleteMutation = useMutation({
    mutationFn: () => deleteHolviIntegration({ slug }),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'holvi'] })
        ])
        toaster.success(t('general.success'))
      }
    }
  })

  const showPromotionCard =
    business?.form === BusinessForms.FI_OY ||
    business?.form === BusinessForms.FI_TMI ||
    business?.form === BusinessForms.FI_OYJ ||
    business?.form === BusinessForms.FI_AS_OY ||
    business?.form === BusinessForms.FI_KOY

  const holviSettingsRedirectUrl = useMemo(
    () => getAbsoluteUrl(getSettingsPageUrl(slug, { page: 'holvi' })),
    [slug]
  )

  return (
    <SettingsSubPageLayout
      title={t('settings.holvi.header')}
      subTitle={null}
      menuItems={[
        {
          Icon: FaTrash,
          intent: 'danger',
          name: t('general.delete'),
          onClick: () => deleteMutation.mutate()
        }
      ]}
    >
      <FeatureWrapper feature="holvi_integration">
        {business?.holvi_bookkeeping_api_enabled ? (
          <AnimatedContentLoader isLoading={isAccountsLoading}>
            {accounts && (
              <HolviAccountMapper
                accounts={accounts}
                showReconnect={business.holvi_bookkeeping_api_staled}
                reconnectRedirectUrl={holviSettingsRedirectUrl}
              />
            )}
          </AnimatedContentLoader>
        ) : (
          <>
            <p>{t('settings.holvi.description')}</p>
            <p>{t('settings.holvi.description1')}</p>
            <p>
              <Trans
                i18nKey={t('settings.holvi.description2')}
                components={[
                  <a
                    key="holvi-link"
                    href={t('link.docs.setupHolviConnection')}
                    target="_blank"
                    rel="noreferrer"
                  />
                ]}
              ></Trans>
            </p>
            <HolviAuthorizeButton />

            {showPromotionCard && (
              <FeatureWrapper feature="holvi_business_accounts">
                <PromoCardWrapper>
                  <h4>{t('settings.holvi.promoCardTitle')}</h4>
                  <HolviPromoCard />
                </PromoCardWrapper>
              </FeatureWrapper>
            )}
          </>
        )}
      </FeatureWrapper>
    </SettingsSubPageLayout>
  )
}

const PromoCardWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl}rem;

  h4 {
    margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  }
`
